import {
  action, flow, makeObservable, observable,
} from 'mobx';

import { CaseAdapter } from '@shared/CaseAdapter';
import BaseStore from './BaseStore';
import DemarchesAPI from '../api/demarches_api';
import DemarcheCasesAPI from '../api/demarche_cases_api';
import DemarcheDocumentsAPI from '../api/demarche_documents_api';
import OrdersAPI from '../api/orders_api';
import {
  mapContactInfoToContactPayload, mapContactInfoToOrderPatchPayload,
} from '../components/DemarchesPermis/funnel/contactMapping';
import { mapAnswersToOrderPayload } from '../components/DemarchesPermis/funnel/orderMapping';
import { mapSubmitPayload } from '../components/DemarchesPermis/funnel/submitMapping';
import {
  AddressInfo, CaseSummary, CivilState, ContactInfo, Consents, DemarcheChoice, DemarcheFlowConfig,
  DocumentsInfo, ExtraFieldValues, HandledBy, OrderInfo, PaymentInfo, UploadedDocument,
} from '../components/DemarchesPermis/types';

// The document checklist depends on nationalité/pays de naissance/type d'adresse — and, per
// procedure, on extra fields (recensement/JDC/ASSR) — all recomputed server-side by the same PATCH
// used at final submit. Debounced so section 4 of the ANTS form can show a live checklist without
// firing a PATCH per keystroke.
const CHECKLIST_REFRESH_DEBOUNCE_MS = 500;

const EMPTY_CONTACT_INFO: ContactInfo = {
  civilite: '',
  prenom: '',
  nom: '',
  email: '',
  telephone: '',
};

const EMPTY_CIVIL_STATE: CivilState = {
  nationalite: '',
  dateNaissance: '',
  villeNaissance: '',
  paysNaissance: '',
};

const EMPTY_ADDRESS: AddressInfo = {
  adresse: '',
  complement: '',
  cp: '',
  ville: '',
  typeAdresse: '',
};

const EMPTY_CONSENTS: Consents = {
  accuracy: false,
  cgvMandate: false,
  earlyExecution: false,
  independenceFromAnts: false,
};

const contactInfoEqual = (a: ContactInfo | null, b: ContactInfo): boolean => (
  !!a && a.civilite === b.civilite && a.prenom === b.prenom && a.nom === b.nom
    && a.email === b.email && a.telephone === b.telephone
);

// Named rather than numeric because the funnel branches (paid vs free, with/without an accompagnement
// offer at all) rather than proceeding through a fixed linear sequence — see DemarcheFunnelContainer.
export type FunnelStep = 'situation' | 'contact' | 'summary' | 'ants-form' | 'payment' | 'success' | 'free';

class DemarcheFunnelStore extends BaseStore {
  slug = '';
  situation: string | null = null;
  contactInfo: ContactInfo = { ...EMPTY_CONTACT_INFO };
  choice: DemarcheChoice = null;
  civilState: CivilState = { ...EMPTY_CIVIL_STATE };
  address: AddressInfo = { ...EMPTY_ADDRESS };
  consents: Consents = { ...EMPTY_CONSENTS };
  extraFieldValues: ExtraFieldValues = {};
  step: FunnelStep = 'situation';
  config: DemarcheFlowConfig | null = null;
  isConfigLoading = false;
  configErrorStatus: number | null = null;
  order: OrderInfo | null = null;
  // What the order was created (or last patched) with — the order payload itself deliberately
  // carries no contact data back, so this is the only way to tell a re-submitted ContactStep
  // actually changed something worth PATCHing.
  orderContactInfo: ContactInfo | null = null;
  isOrderLoading = false;
  orderErrorStatus: number | null = null;
  orderErrorCode: string | null = null;
  orderErrorMessages: Record<string, string> | null = null;
  isPaymentLoading = false;
  paymentErrorStatus: number | null = null;
  paymentErrorCode: string | null = null;
  handledBy: HandledBy | null = null;
  // The case row's own id (Mongo _id, not caseReference) — needed only to link to F5's dossier
  // detail page, same best-effort/nullable treatment as handledBy (see fetchHandledBy).
  caseId: string | null = null;
  documents: UploadedDocument[] = [];
  documentsComplete = false;
  isDocumentsLoading = false;
  uploadingDocumentKeys: Record<string, boolean> = {};
  documentUploadErrors: Record<string, string> = {};

  // Not observable: a plain timer handle for `scheduleChecklistRefresh`'s debounce, not UI state.
  private checklistRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  private checklistRequestId = 0;

  private inFlightChecklistArgs: [CivilState, AddressInfo, ExtraFieldValues] | null = null;

  constructor() {
    super();

    makeObservable(this, {
      slug: observable,
      situation: observable,
      contactInfo: observable,
      choice: observable,
      civilState: observable,
      address: observable,
      consents: observable,
      extraFieldValues: observable,
      step: observable,
      config: observable,
      isConfigLoading: observable,
      configErrorStatus: observable,
      order: observable,
      orderContactInfo: observable,
      isOrderLoading: observable,
      orderErrorStatus: observable,
      orderErrorCode: observable,
      orderErrorMessages: observable,
      isPaymentLoading: observable,
      paymentErrorStatus: observable,
      paymentErrorCode: observable,
      handledBy: observable,
      caseId: observable,
      documents: observable,
      documentsComplete: observable,
      isDocumentsLoading: observable,
      uploadingDocumentKeys: observable,
      documentUploadErrors: observable,
      applyDocumentsInfo: action,
      applyOrder: action,
      init: action,
      setSituation: action,
      setContactInfo: action,
      setChoice: action,
      setCivilState: action,
      setAddress: action,
      setConsents: action,
      setExtraFieldValues: action,
      goToStep: action,
      reset: action,
    });
  }

  // Fetches the flow's funnel configuration (situation step, ANTS fields, documents, offer) from
  // GET /api/web/v1/demarches/:slug. Kept separate from `init`/`reset`: this only tracks the
  // *configuration*, not the visitor's answers, so a config already in hand for the same slug is
  // not re-fetched, and a failure never touches `slug`/`situation` — those stay owned by
  // DemarcheFunnelContainer, which validates a prefilled situation against the config once it loads.
  fetchConfig = flow(function* fetchConfig(this: DemarcheFunnelStore, slug: string) {
    if (this.config && this.config.slug === slug) {
      return;
    }

    this.isConfigLoading = true;
    this.configErrorStatus = null;

    try {
      const { data } = yield DemarchesAPI.getFlow(slug);
      this.config = CaseAdapter.toFrontFormat(data) as DemarcheFlowConfig;
    } catch (e) {
      this.configErrorStatus = e.response?.status || 500;
      console.error(e);
    } finally {
      this.isConfigLoading = false;
    }
  });

  // Fire-and-forget: POST /demarches/:slug/contacts (backend-plan.md §4.1) exists to give the
  // free/paid split a denominator, answers 201 with no body, and is not readable back. Nothing in
  // the UI depends on its outcome, so a failure is logged rather than surfaced or retried.
  submitContact = flow(function* submitContact(
    this: DemarcheFunnelStore, situation: string | null, contactInfo: ContactInfo,
  ) {
    try {
      yield DemarchesAPI.createContact(this.slug, mapContactInfoToContactPayload(situation, contactInfo));
    } catch (e) {
      console.error(e);
    }
  });

  // POST /demarches/:slug/orders on entering the paid branch (backend-plan.md §7.2). Blocking, unlike
  // submitContact: the ANTS form cannot PATCH without the access_token this returns, so the container
  // awaits the result and only enters the form on success. Idempotent so a user bouncing back to the
  // summary step and choosing "payant" again does not create a second order for the same answer —
  // but if the user went back to the contact step and edited it in between, that change is PATCHed
  // onto the existing order rather than silently dropped (the "Modifier ces informations" link on
  // the ANTS form has to actually do something once an order already exists).
  createOrder = flow(function* createOrder(
    this: DemarcheFunnelStore, situation: string | null, contactInfo: ContactInfo,
  ) {
    if (this.order && this.order.demarcheSlug === this.slug && this.order.situation === situation) {
      if (contactInfoEqual(this.orderContactInfo, contactInfo)) {
        return true;
      }
      return yield this.patchOrderContact(contactInfo);
    }

    this.isOrderLoading = true;
    this.orderErrorStatus = null;
    this.orderErrorCode = null;
    this.orderErrorMessages = null;

    try {
      const { data } = yield OrdersAPI.create(this.slug, mapContactInfoToContactPayload(situation, contactInfo));
      this.applyOrder(data);
      this.orderContactInfo = { ...contactInfo };
      return true;
    } catch (e) {
      this.orderErrorStatus = e.response?.status || 500;
      console.error(e);
      return false;
    } finally {
      this.isOrderLoading = false;
    }
  });

  patchOrderContact = flow(function* patchOrderContact(this: DemarcheFunnelStore, contactInfo: ContactInfo) {
    if (!this.order) {
      return false;
    }

    this.isOrderLoading = true;
    this.orderErrorStatus = null;
    this.orderErrorCode = null;
    this.orderErrorMessages = null;

    try {
      const { data } = yield OrdersAPI.update(
        this.slug,
        this.order.accessToken,
        mapContactInfoToOrderPatchPayload(contactInfo),
      );
      this.applyOrder(data);
      this.orderContactInfo = { ...contactInfo };
      return true;
    } catch (e) {
      this.orderErrorStatus = e.response?.status || 500;
      console.error(e);
      return false;
    } finally {
      this.isOrderLoading = false;
    }
  });

  // PATCH /demarches/:slug/orders/:token on the ANTS form's submit. Blocking for the same reason as
  // createOrder: payment must not proceed on answers that failed to save.
  updateOrder = flow(function* updateOrder(
    this: DemarcheFunnelStore, civilState: CivilState, address: AddressInfo, extraFieldValues: ExtraFieldValues,
  ) {
    if (!this.order) {
      return false;
    }

    this.cancelChecklistRefresh();
    this.isOrderLoading = true;
    this.orderErrorStatus = null;
    this.orderErrorCode = null;
    this.orderErrorMessages = null;

    try {
      const { data } = yield OrdersAPI.update(
        this.slug,
        this.order.accessToken,
        mapAnswersToOrderPayload(civilState, address, extraFieldValues),
      );
      this.applyOrder(data);
      return true;
    } catch (e) {
      this.orderErrorStatus = e.response?.status || 500;
      console.error(e);
      return false;
    } finally {
      this.isOrderLoading = false;
    }
  });

  // Silent variant of `updateOrder`, used only to keep `order.documentChecklist` live while the
  // ANTS form is being filled in — deliberately doesn't touch `isOrderLoading`/`orderErrorStatus`
  // (those gate the blocking submit flow) so a background refresh never disables the form or
  // surfaces an error banner for what is, from the user's point of view, just typing. A real
  // failure resurfaces at final submit, which reuses the same endpoint.
  refreshChecklist = flow(function* refreshChecklist(
    this: DemarcheFunnelStore, civilState: CivilState, address: AddressInfo, extraFieldValues: ExtraFieldValues,
  ) {
    if (!this.order) {
      return;
    }

    this.checklistRequestId += 1;
    const requestId = this.checklistRequestId;
    this.inFlightChecklistArgs = [civilState, address, extraFieldValues];

    try {
      const { data } = yield OrdersAPI.update(
        this.slug,
        this.order.accessToken,
        mapAnswersToOrderPayload(civilState, address, extraFieldValues),
      );
      if (requestId === this.checklistRequestId) {
        this.applyOrder(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (requestId === this.checklistRequestId) {
        this.inFlightChecklistArgs = null;
      }
    }
  });

  // Debounced entry point for `refreshChecklist`, called on every change to the fields the
  // checklist depends on (nationalité, pays de naissance, type d'adresse, extra fields).
  scheduleChecklistRefresh = (
    civilState: CivilState, address: AddressInfo, extraFieldValues: ExtraFieldValues,
  ): void => {
    if (this.checklistRefreshTimer) {
      clearTimeout(this.checklistRefreshTimer);
    }
    this.checklistRefreshTimer = setTimeout(() => {
      this.refreshChecklist(civilState, address, extraFieldValues);
    }, CHECKLIST_REFRESH_DEBOUNCE_MS);
  };

  cancelChecklistRefresh = (): void => {
    if (this.checklistRefreshTimer) {
      clearTimeout(this.checklistRefreshTimer);
      this.checklistRefreshTimer = null;
    }
    this.checklistRequestId += 1;
    this.inFlightChecklistArgs = null;
  };

  // POST /demarches/:slug/orders/:token/submit — the ANTS form's account section (§F2a): records
  // the four consents and registers or attaches the student. `password` is omitted for a visitor
  // already signed in (DemarcheFunnelContainer decides which). Errors carry `errorCode`
  // (`existing_email`) and/or field messages (e.g. a too-short password) that the container reads
  // off `orderErrorCode`/`orderErrorMessages` to branch the form rather than showing the generic
  // banner.
  submitOrder = flow(function* submitOrder(
    this: DemarcheFunnelStore, consents: Consents, password?: string, isMinor = false,
  ) {
    if (!this.order) {
      return false;
    }

    this.isOrderLoading = true;
    this.orderErrorStatus = null;
    this.orderErrorCode = null;
    this.orderErrorMessages = null;

    try {
      const { data } = yield OrdersAPI.submit(
        this.slug,
        this.order.accessToken,
        mapSubmitPayload(consents, password, isMinor),
      );
      this.applyOrder(data);
      return true;
    } catch (e) {
      this.orderErrorStatus = e.response?.status || 500;
      this.orderErrorCode = e.response?.data?.errorCode || null;
      this.orderErrorMessages = e.response?.data?.errorMessages || null;
      console.error(e);
      return false;
    } finally {
      this.isOrderLoading = false;
    }
  });

  // GET /demarches/:slug/orders/:token — restores an order the user is coming back to, either from
  // Stripe's return redirect (?resume=<token>) or (later) a genuine resume link. Unlike createOrder
  // this has no idempotency guard to skip: it always re-fetches, because the whole point is to pick
  // up whatever the backend now holds (a payment the webhook may just have recorded).
  resumeOrder = flow(function* resumeOrder(this: DemarcheFunnelStore, slug: string, token: string) {
    this.isOrderLoading = true;
    this.orderErrorStatus = null;
    this.orderErrorCode = null;
    this.orderErrorMessages = null;

    try {
      const { data } = yield OrdersAPI.show(slug, token);
      this.applyOrder(data);
      this.situation = this.order.situation;
      return true;
    } catch (e) {
      this.orderErrorStatus = e.response?.status || 500;
      console.error(e);
      return false;
    } finally {
      this.isOrderLoading = false;
    }
  });

  // POST .../orders/:token/payment — hands back Stripe's hosted Checkout URL and navigates there
  // directly (document.location.href), the same shape BookingStore.makeBooking already uses for
  // exam-booking payments. The order stays `created` until the webhook says otherwise; nothing here
  // asserts the payment succeeded.
  createPayment = flow(function* createPayment(this: DemarcheFunnelStore) {
    if (!this.order) {
      return false;
    }

    this.isPaymentLoading = true;
    this.paymentErrorStatus = null;
    this.paymentErrorCode = null;

    try {
      const { data } = yield OrdersAPI.pay(this.slug, this.order.accessToken);
      const { paymentUrl } = CaseAdapter.toFrontFormat(data) as PaymentInfo;
      document.location.href = paymentUrl;
      return true;
    } catch (e) {
      this.paymentErrorStatus = e.response?.status || 500;
      this.paymentErrorCode = e.response?.data?.errorCode || null;
      console.error(e);
      return false;
    } finally {
      this.isPaymentLoading = false;
    }
  });

  // Best-effort only: the order endpoint deliberately serves no assignee data (it is reachable by
  // anyone holding the token — DemarcheOrderSerializer's own doc comment). The partner's name is
  // only on the student's own case list, which `submit` already signed the visitor into. A failure,
  // an unmatched case (webhook hasn't assigned an agent yet) or a signed-out visitor all leave
  // `handledBy` null silently — the confirmation screen must not depend on this succeeding.
  fetchHandledBy = flow(function* fetchHandledBy(this: DemarcheFunnelStore) {
    if (!this.order?.caseReference) {
      return;
    }

    try {
      const { data } = yield DemarcheCasesAPI.list();
      const { cases } = CaseAdapter.toFrontFormat(data) as { cases: CaseSummary[] };
      const match = cases.find((item) => item.caseReference === this.order?.caseReference);
      this.handledBy = match?.handledBy || null;
      this.caseId = match?.id || null;
    } catch (e) {
      console.error(e);
    }
  });

  applyOrder = (data: Record<string, unknown>): void => {
    this.order = CaseAdapter.toFrontFormat(data) as OrderInfo;
    this.documentsComplete = this.order.documentsComplete;
  };

  applyDocumentsInfo = (data: Record<string, unknown>): void => {
    const info = CaseAdapter.toFrontFormat(data) as DocumentsInfo;
    this.checklistRequestId += 1;
    if (this.inFlightChecklistArgs) {
      const [civilState, address, extraFieldValues] = this.inFlightChecklistArgs;
      this.inFlightChecklistArgs = null;
      this.scheduleChecklistRefresh(civilState, address, extraFieldValues);
    }
    this.documents = info.documents;
    this.documentsComplete = info.documentsComplete;
    if (this.order) {
      this.order = {
        ...this.order,
        documentChecklist: info.documentChecklist,
        documentsComplete: info.documentsComplete,
      };
    }
  };

  // GET .../orders/:token/documents — hydrates the ANTS form's document section on mount, so a
  // reloaded/resumed order shows what was already uploaded rather than starting empty.
  fetchDocuments = flow(function* fetchDocuments(this: DemarcheFunnelStore) {
    if (!this.order) {
      return;
    }

    this.isDocumentsLoading = true;

    try {
      const { data } = yield DemarcheDocumentsAPI.index(this.slug, this.order.accessToken);
      this.applyDocumentsInfo(data);
    } catch (e) {
      console.error(e);
    } finally {
      this.isDocumentsLoading = false;
    }
  });

  uploadDocument = flow(function* uploadDocument(this: DemarcheFunnelStore, documentKey: string, file: File) {
    if (!this.order) {
      return false;
    }

    this.uploadingDocumentKeys = { ...this.uploadingDocumentKeys, [documentKey]: true };
    const { [documentKey]: _removedError, ...remainingErrors } = this.documentUploadErrors;
    this.documentUploadErrors = remainingErrors;

    try {
      const { data } = yield DemarcheDocumentsAPI.create(this.slug, this.order.accessToken, documentKey, file);
      this.applyDocumentsInfo(data);
      return true;
    } catch (e) {
      const message = e.response?.data?.errorMessages?.message
        || 'Une erreur est survenue lors de l’envoi du document.';
      this.documentUploadErrors = { ...this.documentUploadErrors, [documentKey]: message };
      console.error(e);
      return false;
    } finally {
      const { [documentKey]: _uploading, ...remainingUploads } = this.uploadingDocumentKeys;
      this.uploadingDocumentKeys = remainingUploads;
    }
  });

  deleteDocument = flow(function* deleteDocument(this: DemarcheFunnelStore, id: string) {
    if (!this.order) {
      return false;
    }

    try {
      const { data } = yield DemarcheDocumentsAPI.destroy(this.slug, this.order.accessToken, id);
      this.applyDocumentsInfo(data);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  });

  // `situation` is prefilled when the funnel is entered from a service page's selector
  // (/demarches-permis/tunnel?demarche=…&situation=…), so step 1 is not asked twice.
  init = (slug: string, situation: string | null = null): void => {
    if (this.slug === slug) {
      return;
    }

    this.reset();
    this.slug = slug;
    this.situation = situation;
  };

  setSituation = (situation: string): void => {
    this.situation = situation;
  };

  setContactInfo = (contactInfo: ContactInfo): void => {
    this.contactInfo = { ...contactInfo };
  };

  setChoice = (choice: DemarcheChoice): void => {
    this.choice = choice;
  };

  setCivilState = (civilState: CivilState): void => {
    this.civilState = { ...civilState };
  };

  setAddress = (address: AddressInfo): void => {
    this.address = { ...address };
  };

  setConsents = (consents: Consents): void => {
    this.consents = { ...consents };
  };

  setExtraFieldValues = (values: ExtraFieldValues): void => {
    this.extraFieldValues = { ...values };
  };

  goToStep = (step: FunnelStep): void => {
    this.step = step;
  };

  reset = (): void => {
    this.cancelChecklistRefresh();
    this.slug = '';
    this.situation = null;
    this.contactInfo = { ...EMPTY_CONTACT_INFO };
    this.choice = null;
    this.civilState = { ...EMPTY_CIVIL_STATE };
    this.address = { ...EMPTY_ADDRESS };
    this.consents = { ...EMPTY_CONSENTS };
    this.extraFieldValues = {};
    this.step = 'situation';
    this.order = null;
    this.orderContactInfo = null;
    this.orderErrorStatus = null;
    this.orderErrorCode = null;
    this.orderErrorMessages = null;
    this.isPaymentLoading = false;
    this.paymentErrorStatus = null;
    this.paymentErrorCode = null;
    this.handledBy = null;
    this.caseId = null;
    this.documents = [];
    this.documentsComplete = false;
    this.isDocumentsLoading = false;
    this.uploadingDocumentKeys = {};
    this.documentUploadErrors = {};
  };
}

export {
  DemarcheFunnelStore, EMPTY_CONTACT_INFO, EMPTY_CIVIL_STATE, EMPTY_ADDRESS, EMPTY_CONSENTS,
};

export default new DemarcheFunnelStore();
